// 🎨 Temas Revolucionarios - Sistema de Diseño
import { temaMarca } from '@brand/tema';

export interface Theme {
  id: string;
  name: string;
  description: string;
  category: 'light' | 'dark' | 'special';
  colors: {
    background: string;
    foreground: string;
    card: string;
    cardForeground: string;
    popover: string;
    popoverForeground: string;
    primary: string;
    primaryForeground: string;
    secondary: string;
    secondaryForeground: string;
    muted: string;
    mutedForeground: string;
    accent: string;
    accentForeground: string;
    destructive: string;
    destructiveForeground: string;
    border: string;
    input: string;
    ring: string;
  };
}

export const themes: Record<string, Theme> = {
  // 🩺 Tema Clínico (Por defecto) — petróleo + coral, blanco cálido
  'clinico': {
    id: 'clinico',
    name: 'Clínico',
    description: 'Identidad clínica: petróleo y coral, legible y cálida',
    category: 'light',
    colors: {
      background: '36 33% 97%',
      foreground: '196 19% 15%',
      card: '40 40% 99%',
      cardForeground: '196 19% 15%',
      popover: '40 40% 99%',
      popoverForeground: '196 19% 15%',
      primary: '174 62% 28%',
      primaryForeground: '40 40% 98%',
      secondary: '36 22% 92%',
      secondaryForeground: '196 19% 18%',
      muted: '36 22% 93%',
      mutedForeground: '196 9% 38%',
      accent: '8 78% 62%',
      accentForeground: '0 0% 100%',
      destructive: '0 72% 48%',
      destructiveForeground: '0 0% 100%',
      border: '36 18% 87%',
      input: '36 18% 87%',
      ring: '174 62% 28%',
    },
  },

  // 🌙 Tema Clínico · Noche (turnos nocturnos)
  'clinico-noche': {
    id: 'clinico-noche',
    name: 'Clínico · Noche',
    description: 'Modo nocturno clínico: gris azulado profundo, no negro',
    category: 'dark',
    colors: {
      background: '196 24% 8%',
      foreground: '180 12% 90%',
      card: '196 22% 11%',
      cardForeground: '180 12% 90%',
      popover: '196 22% 11%',
      popoverForeground: '180 12% 90%',
      primary: '172 60% 48%',
      primaryForeground: '196 30% 8%',
      secondary: '196 18% 18%',
      secondaryForeground: '180 12% 90%',
      muted: '196 18% 16%',
      mutedForeground: '180 8% 60%',
      accent: '8 75% 64%',
      accentForeground: '196 30% 8%',
      destructive: '0 62% 50%',
      destructiveForeground: '0 0% 100%',
      border: '196 16% 20%',
      input: '196 16% 20%',
      ring: '172 60% 48%',
    },
  },

  // 🌊 Tema Fluid Glass
  'fluid-glass': {
    id: 'fluid-glass',
    name: 'Fluid Glass',
    description: 'Glassmorphism moderno con gradientes fluidos',
    category: 'light',
    colors: {
      background: '0 0% 100%',
      foreground: '222.2 84% 4.9%',
      card: '0 0% 100%',
      cardForeground: '222.2 84% 4.9%',
      popover: '0 0% 100%',
      popoverForeground: '222.2 84% 4.9%',
      primary: '221.2 83.2% 53.3%',
      primaryForeground: '210 40% 98%',
      secondary: '210 40% 96.1%',
      secondaryForeground: '222.2 47.4% 11.2%',
      muted: '210 40% 96.1%',
      mutedForeground: '215.4 16.3% 46.9%',
      accent: '210 40% 96.1%',
      accentForeground: '222.2 47.4% 11.2%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '210 40% 98%',
      border: '214.3 31.8% 91.4%',
      input: '214.3 31.8% 91.4%',
      ring: '221.2 83.2% 53.3%',
    },
  },

  // 🌃 Tema Dark Professional
  'dark-professional': {
    id: 'dark-professional',
    name: 'Dark Professional',
    description: 'Oscuro elegante para trabajo nocturno',
    category: 'dark',
    colors: {
      background: '222.2 84% 4.9%',
      foreground: '210 40% 98%',
      card: '222.2 84% 4.9%',
      cardForeground: '210 40% 98%',
      popover: '222.2 84% 4.9%',
      popoverForeground: '210 40% 98%',
      primary: '217.2 91.2% 59.8%',
      primaryForeground: '222.2 47.4% 11.2%',
      secondary: '217.2 32.6% 17.5%',
      secondaryForeground: '210 40% 98%',
      muted: '217.2 32.6% 17.5%',
      mutedForeground: '215 20.2% 65.1%',
      accent: '217.2 32.6% 17.5%',
      accentForeground: '210 40% 98%',
      destructive: '0 62.8% 30.6%',
      destructiveForeground: '210 40% 98%',
      border: '217.2 32.6% 17.5%',
      input: '217.2 32.6% 17.5%',
      ring: '224.3 76.3% 48%',
    },
  },

  // 🎨 Tema Cyberpunk
  'neon-cyberpunk': {
    id: 'neon-cyberpunk',
    name: 'Neon Cyberpunk',
    description: 'Neones vibrantes estilo futurista',
    category: 'dark',
    colors: {
      background: '240 10% 3.9%',
      foreground: '0 0% 98%',
      card: '240 10% 3.9%',
      cardForeground: '0 0% 98%',
      popover: '240 10% 3.9%',
      popoverForeground: '0 0% 98%',
      primary: '326 100% 74%', // Neon pink
      primaryForeground: '240 10% 3.9%',
      secondary: '191 100% 50%', // Cyan
      secondaryForeground: '240 10% 3.9%',
      muted: '240 3.7% 15.9%',
      mutedForeground: '240 5% 64.9%',
      accent: '191 100% 50%',
      accentForeground: '240 10% 3.9%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '0 0% 98%',
      border: '240 3.7% 15.9%',
      input: '240 3.7% 15.9%',
      ring: '326 100% 74%',
    },
  },

  // 🌌 Tema Cosmic
  'cosmic-nexus': {
    id: 'cosmic-nexus',
    name: 'Cosmic Nexus',
    description: 'Inspirado en el espacio y galaxias',
    category: 'dark',
    colors: {
      background: '224 71% 4%',
      foreground: '213 31% 91%',
      card: '224 71% 4%',
      cardForeground: '213 31% 91%',
      popover: '224 71% 4%',
      popoverForeground: '213 31% 91%',
      primary: '263 70% 50%', // Purple
      primaryForeground: '210 40% 98%',
      secondary: '217 91% 60%', // Blue
      secondaryForeground: '222.2 47.4% 1.2%',
      muted: '223 47% 11%',
      mutedForeground: '215.4 16.3% 56.9%',
      accent: '263 70% 50%',
      accentForeground: '210 40% 98%',
      destructive: '0 63% 31%',
      destructiveForeground: '210 40% 98%',
      border: '223 47% 11%',
      input: '223 47% 11%',
      ring: '263 70% 50%',
    },
  },

  // 🌿 Tema Nature
  'nature-green': {
    id: 'nature-green',
    name: 'Nature Green',
    description: 'Inspirado en la naturaleza',
    category: 'light',
    colors: {
      background: '0 0% 100%',
      foreground: '240 10% 3.9%',
      card: '0 0% 100%',
      cardForeground: '240 10% 3.9%',
      popover: '0 0% 100%',
      popoverForeground: '240 10% 3.9%',
      primary: '142 76% 36%', // Green
      primaryForeground: '0 0% 98%',
      secondary: '142 76% 45%',
      secondaryForeground: '240 5.9% 10%',
      muted: '142 30% 96%',
      mutedForeground: '240 3.8% 46.1%',
      accent: '142 30% 96%',
      accentForeground: '240 5.9% 10%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '0 0% 98%',
      border: '142 30% 88%',
      input: '142 30% 88%',
      ring: '142 76% 36%',
    },
  },

  // 🎭 Tema Minimal Light
  'minimal-light': {
    id: 'minimal-light',
    name: 'Minimal Light',
    description: 'Minimalista y limpio',
    category: 'light',
    colors: {
      background: '0 0% 100%',
      foreground: '20 14.3% 4.1%',
      card: '0 0% 100%',
      cardForeground: '20 14.3% 4.1%',
      popover: '0 0% 100%',
      popoverForeground: '20 14.3% 4.1%',
      primary: '24 9.8% 10%',
      primaryForeground: '60 9.1% 97.8%',
      secondary: '60 4.8% 95.9%',
      secondaryForeground: '24 9.8% 10%',
      muted: '60 4.8% 95.9%',
      mutedForeground: '25 5.3% 44.7%',
      accent: '60 4.8% 95.9%',
      accentForeground: '24 9.8% 10%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '60 9.1% 97.8%',
      border: '20 5.9% 90%',
      input: '20 5.9% 90%',
      ring: '24 9.8% 10%',
    },
  },

  // 🔥 Tema Sunset
  'sunset-warm': {
    id: 'sunset-warm',
    name: 'Sunset Warm',
    description: 'Colores cálidos de atardecer',
    category: 'light',
    colors: {
      background: '0 0% 100%',
      foreground: '20 14.3% 4.1%',
      card: '0 0% 100%',
      cardForeground: '20 14.3% 4.1%',
      popover: '0 0% 100%',
      popoverForeground: '20 14.3% 4.1%',
      primary: '24 94% 50%', // Orange
      primaryForeground: '60 9.1% 97.8%',
      secondary: '45 93% 47%', // Yellow
      secondaryForeground: '20 14.3% 4.1%',
      muted: '60 4.8% 95.9%',
      mutedForeground: '25 5.3% 44.7%',
      accent: '45 93% 95%',
      accentForeground: '20 14.3% 4.1%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '60 9.1% 97.8%',
      border: '20 5.9% 90%',
      input: '20 5.9% 90%',
      ring: '24 94% 50%',
    },
  },

  // 💜 Tema Purple Dream
  'purple-dream': {
    id: 'purple-dream',
    name: 'Purple Dream',
    description: 'Púrpura elegante y sofisticado',
    category: 'dark',
    colors: {
      background: '280 50% 6%',
      foreground: '280 5% 96%',
      card: '280 50% 6%',
      cardForeground: '280 5% 96%',
      popover: '280 50% 6%',
      popoverForeground: '280 5% 96%',
      primary: '270 95% 75%', // Light purple
      primaryForeground: '280 50% 6%',
      secondary: '280 40% 20%',
      secondaryForeground: '280 5% 96%',
      muted: '280 40% 15%',
      mutedForeground: '280 5% 60%',
      accent: '280 40% 20%',
      accentForeground: '280 5% 96%',
      destructive: '0 63% 31%',
      destructiveForeground: '280 5% 96%',
      border: '280 40% 15%',
      input: '280 40% 15%',
      ring: '270 95% 75%',
    },
  },

  // 🌸 Tema Rose Gold
  'rose-gold': {
    id: 'rose-gold',
    name: 'Rose Gold',
    description: 'Elegante oro rosa con toques metálicos',
    category: 'light',
    colors: {
      background: '340 100% 99%',
      foreground: '340 40% 10%',
      card: '340 100% 99%',
      cardForeground: '340 40% 10%',
      popover: '340 100% 99%',
      popoverForeground: '340 40% 10%',
      primary: '340 82% 52%', // Rose gold
      primaryForeground: '0 0% 100%',
      secondary: '340 40% 94%',
      secondaryForeground: '340 40% 10%',
      muted: '340 30% 96%',
      mutedForeground: '340 10% 45%',
      accent: '340 40% 94%',
      accentForeground: '340 40% 10%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '0 0% 98%',
      border: '340 30% 90%',
      input: '340 30% 90%',
      ring: '340 82% 52%',
    },
  },

  // 🌊 Tema Ocean Blue
  'ocean-blue': {
    id: 'ocean-blue',
    name: 'Ocean Blue',
    description: 'Azul océano profundo y refrescante',
    category: 'dark',
    colors: {
      background: '210 50% 10%',
      foreground: '210 40% 98%',
      card: '210 50% 10%',
      cardForeground: '210 40% 98%',
      popover: '210 50% 10%',
      popoverForeground: '210 40% 98%',
      primary: '199 89% 48%', // Ocean blue
      primaryForeground: '0 0% 100%',
      secondary: '210 40% 20%',
      secondaryForeground: '210 40% 98%',
      muted: '210 40% 15%',
      mutedForeground: '210 20% 65%',
      accent: '210 40% 20%',
      accentForeground: '210 40% 98%',
      destructive: '0 63% 31%',
      destructiveForeground: '210 40% 98%',
      border: '210 40% 18%',
      input: '210 40% 18%',
      ring: '199 89% 48%',
    },
  },

  // 🍂 Tema Autumn
  'autumn-leaves': {
    id: 'autumn-leaves',
    name: 'Autumn Leaves',
    description: 'Colores cálidos de otoño',
    category: 'light',
    colors: {
      background: '30 30% 98%',
      foreground: '30 30% 10%',
      card: '30 30% 98%',
      cardForeground: '30 30% 10%',
      popover: '30 30% 98%',
      popoverForeground: '30 30% 10%',
      primary: '25 75% 47%', // Burnt orange
      primaryForeground: '0 0% 100%',
      secondary: '45 60% 88%',
      secondaryForeground: '30 30% 10%',
      muted: '45 30% 95%',
      mutedForeground: '30 20% 45%',
      accent: '45 60% 88%',
      accentForeground: '30 30% 10%',
      destructive: '0 84.2% 60.2%',
      destructiveForeground: '0 0% 98%',
      border: '45 30% 88%',
      input: '45 30% 88%',
      ring: '25 75% 47%',
    },
  },

  // 🌙 Tema Midnight
  'midnight-blue': {
    id: 'midnight-blue',
    name: 'Midnight Blue',
    description: 'Azul medianoche profundo y misterioso',
    category: 'dark',
    colors: {
      background: '220 45% 8%',
      foreground: '220 15% 95%',
      card: '220 45% 8%',
      cardForeground: '220 15% 95%',
      popover: '220 45% 8%',
      popoverForeground: '220 15% 95%',
      primary: '217 91% 60%', // Bright blue
      primaryForeground: '220 45% 8%',
      secondary: '220 30% 18%',
      secondaryForeground: '220 15% 95%',
      muted: '220 30% 15%',
      mutedForeground: '220 15% 60%',
      accent: '220 30% 18%',
      accentForeground: '220 15% 95%',
      destructive: '0 63% 31%',
      destructiveForeground: '220 15% 95%',
      border: '220 30% 18%',
      input: '220 30% 18%',
      ring: '217 91% 60%',
    },
  },

  // 🛡️ Stark HUD — cian arc-reactor + dorado Iron Man sobre azul-noche (J.A.R.V.I.S.)
  'stark-hud': {
    id: 'stark-hud',
    name: 'Stark HUD',
    description: 'Interfaz holográfica: cian neón del arc reactor y dorado Iron Man sobre azul-noche',
    category: 'dark',
    colors: {
      background: '205 45% 6%',
      foreground: '190 30% 92%',
      card: '205 40% 10%',
      cardForeground: '190 30% 92%',
      popover: '205 42% 9%',
      popoverForeground: '190 30% 92%',
      primary: '188 95% 55%',            // cian arc-reactor
      primaryForeground: '205 60% 8%',
      secondary: '205 30% 16%',
      secondaryForeground: '190 25% 88%',
      muted: '205 28% 14%',
      mutedForeground: '200 20% 62%',
      accent: '42 100% 60%',             // dorado Iron Man
      accentForeground: '205 60% 8%',
      destructive: '0 90% 62%',          // rojo repulsor
      destructiveForeground: '0 0% 100%',
      border: '195 55% 20%',
      input: '205 30% 16%',
      ring: '188 95% 60%',
    },
  },
};

// Temas propios de la marca de la instalación (TrackConstruction: Blueprint & Steel).
Object.assign(themes, temaMarca.temas ?? {});

export const defaultTheme = temaMarca.temaPorDefecto ?? 'clinico';

/** Temas que ofrece el selector en esta instalación (K2E: todos). */
export const temasPermitidos = (t: Theme) => !temaMarca.temasPermitidos || temaMarca.temasPermitidos.includes(t.id);

export function getTheme(themeId: string): Theme {
  return themes[themeId] || themes[defaultTheme];
}

export function getThemesList(): Theme[] {
  return Object.values(themes);
}

export function getThemesByCategory(category: Theme['category']): Theme[] {
  return Object.values(themes).filter((theme) => theme.category === category);
}

// Export como array para compatibilidad
export const THEMES = Object.values(themes).filter(temasPermitidos);
